import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { X } from 'lucide-react';

interface MobileCalendarProps {
  startDate: Date | null;
  endDate: Date | null;
  onChange: (dates: [Date | null, Date | null]) => void;
  minDate?: Date;
  maxDate?: Date;
  filterDate?: (date: Date) => boolean;
}

const MobileCalendar: React.FC<MobileCalendarProps> = ({
  startDate,
  endDate,
  onChange,
  minDate,
  maxDate,
  filterDate,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState<Date | null>(startDate);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(endDate);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Store original styles
      const originalStyle = window.getComputedStyle(document.body).overflow;
      const originalPosition = window.getComputedStyle(document.body).position;
      
      // Apply multiple scroll lock techniques
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      document.documentElement.style.overflow = 'hidden';
      
      // Also add CSS class as backup
      document.body.classList.add('modal-open');
      document.documentElement.classList.add('modal-open');
      
      // Store original values for cleanup
      document.body.setAttribute('data-original-overflow', originalStyle);
      document.body.setAttribute('data-original-position', originalPosition);
    } else {
      // Restore original styles
      const originalOverflow = document.body.getAttribute('data-original-overflow') || 'unset';
      const originalPosition = document.body.getAttribute('data-original-position') || 'static';
      
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.width = '';
      document.body.style.height = '';
      document.documentElement.style.overflow = '';
      
      // Remove CSS classes
      document.body.classList.remove('modal-open');
      document.documentElement.classList.remove('modal-open');
      
      // Clean up attributes
      document.body.removeAttribute('data-original-overflow');
      document.body.removeAttribute('data-original-position');
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.position = 'static';
      document.body.style.width = '';
      document.body.style.height = '';
      document.documentElement.style.overflow = '';
      document.body.classList.remove('modal-open');
      document.documentElement.classList.remove('modal-open');
    };
  }, [isOpen]);

  // Format date for display
  const formatDate = (date: Date | null) => {
    if (!date) return '';
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short' 
    });
  };

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setTempStartDate(start);
    setTempEndDate(end);
    
    // If both dates are selected, close the modal and apply changes
    if (start && end) {
      onChange([start, end]);
      setIsOpen(false);
    }
  };

  const handleCancel = () => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setIsOpen(false);
  };

  const handleConfirm = () => {
    onChange([tempStartDate, tempEndDate]);
    setIsOpen(false);
  };

  useEffect(() => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
  }, [startDate, endDate]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Store current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    // Cleanup function
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [isOpen]);

  return (
    <>
      {/* Compact Date Display - Tappable */}
      <div 
        className="flex justify-between w-full gap-4 box-border cursor-pointer"
        onClick={() => setIsOpen(true)}
      >
        <div className="relative flex-1 box-border">
          <div className="relative">
            <div className="w-full h-12 border border-gray-300 rounded-lg bg-white hover:border-gray-400 transition-all duration-100 flex items-center px-4">
              <div className={`absolute left-4 pointer-events-none transform-gpu font-buenos-aires transition-all ${
                startDate 
                  ? 'text-xs text-gray-500 -translate-y-2' 
                  : 'text-base text-gray-500'
              }`}>
                Start date
              </div>
              {startDate && (
                <div className="text-sm text-black font-buenos-aires mt-2">
                  {formatDate(startDate)}
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="relative flex-1 box-border">
          <div className="relative">
            <div className="w-full h-12 border border-gray-300 rounded-lg bg-white hover:border-gray-400 transition-all duration-100 flex items-center px-4">
              <div className={`absolute left-4 pointer-events-none transform-gpu font-buenos-aires transition-all ${
                endDate 
                  ? 'text-xs text-gray-500 -translate-y-2' 
                  : 'text-base text-gray-500'
              }`}>
                End date
              </div>
              {endDate && (
                <div className="text-sm text-black font-buenos-aires mt-2">
                  {formatDate(endDate)}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Sheet Calendar */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-x-0 bg-black bg-opacity-25 z-40"
            style={{ top: '25vh', bottom: '0' }}
            onClick={handleCancel}
          />
          
          {/* Top Sheet */}
          <div className="fixed inset-x-0 bg-white z-50 flex flex-col rounded-b-3xl shadow-2xl" 
               style={{ top: '25vh', height: '75vh', maxHeight: '75vh' }}>
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
            </div>
            
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <button
                onClick={handleCancel}
                className="flex items-center text-gray-600 hover:text-gray-800"
              >
                <X size={24} />
              </button>
              <h2 className="text-lg font-semibold font-buenos-aires">Select Dates</h2>
              <button
                onClick={handleConfirm}
                className="text-blue-500 font-semibold disabled:text-gray-400"
                disabled={!tempStartDate || !tempEndDate}
              >
                Done
              </button>
            </div>

            {/* Selected Dates Display */}
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <div className="flex justify-between text-sm">
                <div>
                  <span className="text-gray-600">Check-in: </span>
                  <span className="font-semibold">
                    {tempStartDate ? formatDate(tempStartDate) : 'Select date'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Check-out: </span>
                  <span className="font-semibold">
                    {tempEndDate ? formatDate(tempEndDate) : 'Select date'}
                  </span>
                </div>
              </div>
            </div>

            {/* Vertically Scrollable Calendar */}
            <div className="flex-1 overflow-y-auto">
              <div className="vertical-calendar-container">
                {/* Generate 6 months starting from current month */}
                {Array.from({ length: 6 }, (_, index) => {
                  const currentDate = new Date();
                  const monthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + index, 1);
                  
                  return (
                    <div key={index} className="month-section">
                      <DatePicker
                        selectsRange={true}
                        startDate={tempStartDate || undefined}
                        endDate={tempEndDate || undefined}
                        onChange={handleDateChange}
                        monthsShown={1}
                        inline
                        calendarStartDay={1}
                        openToDate={monthDate}
                        calendarClassName="mobile-vertical-calendar border-none shadow-none w-full"
                        minDate={minDate}
                        maxDate={maxDate}
                        filterDate={filterDate}
                        showPopperArrow={false}
                        renderCustomHeader={({ date }) => (
                          <div className="text-center py-6 bg-white">
                            <div className="text-xl font-bold text-gray-900">
                              {date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                            </div>
                          </div>
                        )}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default MobileCalendar;